import { proxy, snapshot } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { resetStore } from '@/lib/valtio/reset-store'
import { assert } from '@/utils/assert'
import { parseRundownDate } from '@/utils/parse-rundown-date'

import type { Rundown } from '~shared/rundown/rundown'
import { fastDeepEqual } from '~shared/utils'

import type { Snapshot } from 'valtio'

type RundownState = {
  doc: Rundown | null
}

const initialState: RundownState = {
  doc: null,
}

/**
 * Store for the rundown document (`rundowns/{id}`).
 *
 * Holds the document itself (`doc`), the one place the rest of the rundown page
 * reads its fields from. Ordered cue and column data stay in their own stores;
 * the order ids live here on `rundown.cues` and `rundown.columns`.
 *
 * @remarks
 *   Read-only outside this module. Use `rundownStoreActions.processDoc` for the
 *   Firestore snapshot and `rundownStoreActions.resetStore` on teardown.
 *   Derived values (eg. parent group map) live in `rundown.derived.ts`.
 *
 * @privateRemarks
 *
 *   ### Why merge instead of replace?
 *
 *   Replacing the document on every snapshot would give every subscriber a new
 *   object, so a rename would re-render everything that reads the rundown. The
 *   merge keeps the document's identity and only assigns keys whose value
 *   changed (deep compare, so an unchanged `settings` or `cues` array keeps its
 *   identity too).
 *
 *   ### Why removed keys are deleted explicitly:
 *
 *   Assigning the changed keys alone would leave a key that the document no
 *   longer has (eg. `startCueId` cleared by dropping the field). Such a key
 *   must disappear, not stay with its old value, so every key the store holds
 *   and the incoming document lacks is deleted. A document with a different
 *   `id` (another rundown) is not merged at all: it replaces the store's
 *   document, so none of the old rundown's keys survive.
 *
 * @warning The Firestore listener must stay the ONLY writer. Never write an API
 * response back in as an optimistic update. That is a second writer racing the
 * listener, and an older snapshot arriving after a newer PATCH response silently
 * reverts the user's edit.
 */
const rundownStore = proxy<RundownState>(deepClone(initialState))

/**
 * Parse the date fields of a rundown document into `Date` objects.
 *
 * The Firestore listener already hands over `Date`s, so this is a safety net
 * for ISO strings. A value that does not parse is kept as it arrived.
 */
function parseRundownDates(rundown: Rundown): Rundown {
  return {
    ...rundown,
    startTime: parseRundownDate(rundown.startTime) ?? rundown.startTime,
    endTime: parseRundownDate(rundown.endTime) ?? null,
  }
}

/**
 * Copy one field from `source` to `target`. Generic so each key keeps its own
 * type; a plain `target[key] = source[key]` over a union of keys does not
 * type-check.
 */
function assignField<K extends keyof Rundown>(
  target: Rundown,
  source: Rundown,
  key: K,
) {
  target[key] = source[key]
}

export const rundownStoreActions = {
  /**
   * Syncs the rundown document with the snapshot from the Firestore listener.
   * Listener-only: call it from `rundown-session.ts` and nowhere else.
   *
   * Merges in place: see the privateRemarks on `rundownStore`.
   */
  processDoc(input: Rundown) {
    // Copy first. Valtio turns the nested objects it is given into proxies in
    // place, so the caller's own objects would be left holding proxies, which
    // `structuredClone` cannot copy.
    const next = parseRundownDates(structuredClone(input))

    // Another rundown: nothing carries over, so replace the whole document.
    // Merging would keep the old rundown's keys (eg. `startCueId`) alive.
    if (!rundownStore.doc || rundownStore.doc.id !== next.id) {
      rundownStore.doc = next
      return
    }

    const target = rundownStore.doc
    const current = snapshot(target)

    for (const key of Object.keys(target) as Array<keyof Rundown>) {
      if (!(key in next)) Reflect.deleteProperty(target, key)
    }

    for (const key of Object.keys(next) as Array<keyof Rundown>) {
      if (fastDeepEqual(current[key], next[key])) continue
      assignField(target, next, key)
    }
  },

  /**
   * Empties the store. Called when leaving a rundown.
   */
  resetStore() {
    resetStore(rundownStore, initialState)
  },
}

/**
 * The rundown store itself, for the derived layer and the store hooks.
 *
 * @remarks
 *   Exported as read-only to keep the single-writer rule enforceable by the
 *   type system: subscribing and reading is fine, assigning is not.
 */
export const rundownStoreReadonly: Readonly<RundownState> = rundownStore

/**
 * The rundown document, or null before the first snapshot has arrived.
 *
 * @remarks
 *   Deep readonly: the document is only written by `processDoc`. Components use
 *   the hooks in `rundown.derived.ts`.
 */
export function getRundown(): Snapshot<Rundown> | null {
  return snapshot(rundownStore).doc
}

/**
 * The rundown document, for event handlers and other code that runs only while
 * a rundown is open.
 *
 * @throws If no rundown is loaded. Use `getRundown()` where a missing rundown
 *   is a valid answer.
 */
export function requireRundown(): Snapshot<Rundown> {
  const rundown = snapshot(rundownStore).doc

  assert(rundown, 'requireRundown: no rundown is loaded')
  return rundown
}

/**
 * The id of the rundown on the page, for event handlers and non-React code.
 *
 * @throws If no rundown is loaded. Use `getRundown()?.id` where a missing
 *   rundown is a valid answer.
 */
export function getRundownId(): string {
  return requireRundown().id
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(rundownStore, { name: 'rundownStore', enabled: true })
}
// #endregion
